import { isEmpty } from 'lodash';
import { v4 as uuid } from 'uuid';

import { SearchQuery } from '~/features/advanced-search/types';
import {
  FeedbackDetails as ChatFeedbackDetails,
  Message as ChatMessage,
  Citation,
  CitationEntity,
  MessageSource,
} from '~/features/ask-anything/types/chat';
import { SearchEntity } from '~/features/ask-anything/types/chatbot';
import { postProcessMarkdown } from '~/features/ask-anything/utils/postProcessMarkdown';
import { Entity, EntityType } from '~/types/Entity';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

import {
  createNewConversation,
  getAllConversations,
  getExistingConversation,
  postNewUserMessage,
  postUserRating,
} from '../methods/conversationalAi';
import {
  ConversationMessage,
  ConversationMessageSource,
  ConversationStreamEvent,
  ExternalEntity,
  Conversation as ServerConversation,
  UserRating,
} from '../types/sylvanus/conversation';
import { testIsAxiosError } from '../utils';
import { camelizeKeys, ParsedType } from './utils';

export type Message = ParsedType<ConversationMessage>;
export type Conversation = ParsedType<ServerConversation>;
export type MessageStreamEvent = ParsedType<ConversationStreamEvent>;
export interface ClientConversation {
  id: Uuid;
  created: string;
  createdBy: string;
  userEmail: string;
  messages: ChatMessage[];
  metadata?: {
    documentHash: string;
  };
}

export interface MessageContext {
  search_query?: SearchQuery;
  search_entity: SearchEntity;
  selected_ids?: string[];
  entities_in_view?: Entity<CitationEntity>[];
}
export interface PostUserMessageParams {
  conversationId: Uuid;
  message: string;
  context?: MessageContext[];
  metadata?: {
    questionId: Uuid;
    questionGroupId: Uuid;
  };
}

export interface PostUserFeedbackParams {
  conversationId: Uuid;
  messageId: Uuid;
  feedbackDetails: ChatFeedbackDetails;
}

interface ClientPageConversation {
  items: Omit<Conversation, 'messages'>[];
  total: number;
  page?: number;
  size?: number;
  pages?: number;
}

export const getConversations = async (
  params: Entity<EntityType>,
): Promise<ClientPageConversation> => {
  const serverConversations = await getAllConversations({
    entityId: params.id,
    entityType: params.type as ExternalEntity['type'],
  });
  // TODO remove ts-ignore when server response is fixed
  //@ts-ignore
  return camelizeKeys(serverConversations);
};

export const createConversation = async (
  entity: Entity<EntityType>,
): Promise<Conversation | undefined> => {
  try {
    const response = await createNewConversation(entity as ExternalEntity);
    //@ts-ignore
    return camelizeKeys(response);
  } catch (error: unknown) {
    if (testIsAxiosError(error))
      //i18n-ignore: string needs to be BE handle
      throw new Error('Failed to create conversation', {
        cause: error.response?.status,
      });
  }
};

export const getConversation = async ({
  conversationId,
}: {
  conversationId: Uuid;
}): Promise<ClientConversation> => {
  const response = await getExistingConversation(conversationId);
  const conversation = {
    id: response.id,
    created: response.datetime_created,
    createdBy: response.created_by_user_id,
    userEmail: response.created_by_user_email,
    messages: response.messages.map(mapServerMessageToChatMessage),
    metadata: response.metadata,
  };
  // TODO remove ts-ignore when server response is fixed
  //@ts-ignore
  return conversation;
};

export const postUserMessage = async ({
  conversationId,
  message,
  context,
  metadata,
}: PostUserMessageParams): Promise<ChatMessage> => {
  const response = await postNewUserMessage({
    conversationId,
    message,
    context,
    metadata,
  });
  return mapServerMessageToChatMessage(response);
};

export const postUserFeedback = async ({
  conversationId,
  messageId,
  feedbackDetails,
}: PostUserFeedbackParams): Promise<ChatMessage> => {
  const response = await postUserRating({
    conversationId,
    messageId,
    userRating: mapUserRatingFromFeedbackDetails(feedbackDetails),
    userComment: feedbackDetails.comment,
  });
  return mapServerMessageToChatMessage(response);
};

export const mapServerMessageToChatMessage = (
  response: ConversationMessage,
): ChatMessage => {
  const mappedCitations = mapCitations(response);

  const { citations, text } = postProcessMarkdown(
    response.message_text,
    mappedCitations,
  );
  return {
    choices: response.response_suggestions ?? [],
    id: response.id,
    sources: (response.sources ?? []).map(
      (source: ConversationMessageSource) =>
        ({
          ...camelizeKeys(source),
          id: uuid(),
        }) as unknown as MessageSource,
    ),
    text,
    citations,
    //l10n-ignore: API parsing no formatting required
    timestamp: new Date(response.datetime_created),
    userType: response.role === 'user' ? 'current' : 'other',
    feedbackDetails: mapFeedbackDetailsFromUserRating(
      response.user_rating ?? null,
    ),
  };
};

export const parseServerMessage = (message: Message): ChatMessage => {
  const mappedCitations = mapCitationsFromParsedMessage(message);

  const { citations, text } = postProcessMarkdown(
    message.messageText,
    mappedCitations,
  );
  const parsedMessage: ChatMessage = {
    choices: (message.responseSuggestions ?? []) as string[],
    id: message.id,
    sources: ((message.sources ?? []) as ConversationMessageSource[]).map(
      (source) =>
        ({
          ...camelizeKeys(source),
          id: uuid(),
        }) as unknown as MessageSource,
    ),
    text,
    //l10n-ignore: API parsing no formatting required
    timestamp: new Date(message.datetimeCreated),
    userType: message.role === 'user' ? 'current' : 'other',
    feedbackDetails: mapFeedbackDetailsFromUserRating(
      message.userRating ?? null,
    ),
    citations,
  };

  return parsedMessage;
};

export const mapFeedbackDetailsFromUserRating = (
  rating: Nullable<UserRating>,
): ChatFeedbackDetails => {
  switch (rating) {
    case 'positive':
      return { value: true };
    case 'negative':
      return { value: false };
    default:
      return { value: null };
  }
};

export const mapCitations = (
  message: Pick<ConversationMessage, 'citations'>,
): Record<string, Citation> => {
  if (!message.citations || isEmpty(message.citations)) return {};

  const citationMap: Record<string, Citation> = Object.fromEntries(
    Object.entries(message.citations ?? {}).map(([key, citation]) => [
      key,
      {
        key,
        highlights: citation.highlights.map((highlight) => ({
          id: uuid(),
          coordinates: highlight.coordinates ?? [],
          htmlTokens: highlight.html_tokens ?? [],
        })),
        originalQuote: citation.original_quote,
        entity: citation.entity,
        link: citation.link,
        tooltip: citation.tooltip,
        entityName: citation.entity_name ?? null,
      },
    ]),
  );
  return citationMap;
};

const mapCitationsFromParsedMessage = (
  message: Message,
): Record<string, Citation> => {
  if (!message.citations || isEmpty(message.citations)) return {};

  const citationMap: Record<string, Citation> = Object.fromEntries(
    Object.entries(message.citations ?? {}).map(([key, citation]) => [
      key,
      {
        key,
        highlights: citation.highlights.map((highlight) => ({
          id: uuid(),
          coordinates: highlight.coordinates ?? [],
          htmlTokens: highlight.htmlTokens ?? [],
        })),
        originalQuote: citation.originalQuote,
        entity: citation.entity,
        link: citation.link,
        tooltip: citation.tooltip,
        entityName: citation.entityName,
      } as Citation,
    ]),
  );
  return citationMap;
};

export const mapUserRatingFromFeedbackDetails = (
  details: Nullable<ChatFeedbackDetails> | undefined,
): Nullable<UserRating> => {
  if (!details) return null;
  const { value } = details;
  switch (value) {
    case true:
      return 'positive';
    case false:
      return 'negative';
    default:
      return null;
  }
};
